import React, { useState } from 'react'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import TextField from '@mui/material/TextField'
import Dropzone from './attachment-dropzone'
import SubmitButton from './submit-button'
import Header from './header'
import { fillLegacyTicketForm } from '../../helpers/fill-legacy-ticket-form'
import { LoadNewTicketSuggestion } from '@/javascript/domain/usecases'

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  loadNewTicketSuggestion: LoadNewTicketSuggestion
}

const SuggestionModal: React.FC<Props> = ({ open, setOpen, loadNewTicketSuggestion }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [description, setDescription] = useState('')
  const formatDescription = description.replace(/^[\s\W_]+|[\s\W_]+$/g, "")
  const [files, setFiles] = useState<File[]>([])

  const handleSubmit = async () => {
    if (formatDescription.length === 0 && files.length === 0) return
    setLoading(true)
    setError(false)
    try {
      const result = await loadNewTicketSuggestion.load(formatDescription, files)
      const images = files.filter((f: File) => f.type.startsWith('image/')) || null
      fillLegacyTicketForm({ values: result, images })
      handleClose()
    } catch { setError(true) } finally { setLoading(false) }
  }
  
  const handleClose = (_e?: any, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (loading) return
    if (reason !== 'backdropClick') {
      setOpen(false)
      setDescription('')
      setFiles([])
      setError(false)
    }
  }
  
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="ai-ticket-suggestion-modal-title"
      aria-describedby="ai-ticket-suggestion-modal-description"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'white',
        boxShadow: 24,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        borderRadius: '16px',
        '@media (min-width: 1025px)': {
          width: '32vw',
        },
        width: '80vw',
        minHeight: '60vh'
      }}>
        <Box sx={{
          display: 'flex',
          height: '80px',
          padding: '0',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          alignSelf: 'stretch',
          backgroundColor: '#01A32D',
          borderRadius: '16px 16px 0 0'
        }}>
          <Header onClose={handleClose} />
        </Box>
        <Container sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '16px',
          padding: '32px'
        }}
        >
          <TextField
            label="Descreva todas as informações disponíveis sobre o chamado"
            variant="outlined"
            fullWidth
            multiline
            disabled={loading}
            minRows={8}
            maxRows={12}
            sx={{
              borderRadius: '8px',
            }}
            value={description}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setDescription(event.target.value)
            }}
            slotProps={{
              htmlInput: {
                'data-testid': 'description-input'
              }
            }}
          />
          <Dropzone files={files} setFiles={setFiles} loading={loading} error={error} setError={setError} fileLimit={6} />
        </Container>
        <Container sx={{
            display: 'flex',
            padding: '0 24px 24px 16px',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '16px',
            alignSelf: 'stretch'
          }}
        >
          <SubmitButton
            disabled={(formatDescription.length === 0 && files.length === 0)}
            loading={loading} onClick={handleSubmit}
          />
        </Container>
      </Box>
    </Modal>
  )
}

export default SuggestionModal
